// src/features/admin/components/SingleClinicForm.tsx
import React, { useState } from "react";

export interface SingleClinicFormValues {
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  city?: string | null;
  country?: string | null; // ISO alpha-2
  postcode?: string | null; // NEW: for server geocoding
  website_url?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface SingleClinicFormProps {
  onCreate: (values: SingleClinicFormValues) => Promise<void>;
}

const GOLD = "#f3dfae";

const SingleClinicForm: React.FC<SingleClinicFormProps> = ({ onCreate }) => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("US");
  const [postcode, setPostcode] = useState(""); // NEW
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const handleCreate = async () => {
    setErr("");
    setCreating(true);
    try {
      await onCreate({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        short_description: shortDesc.trim(),
        long_description: longDesc.trim() || undefined,
        city: city.trim() || undefined,
        country: country.trim().toUpperCase() || undefined,
        postcode: postcode.trim() || undefined, // NEW
        website_url: website.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined
      });
      setName("");
      setSlug("");
      setShortDesc("");
      setLongDesc("");
      setCity("");
      setCountry("US");
      setPostcode("");
      setWebsite("");
      setPhone("");
      setEmail("");
    } catch (e: any) {
      setErr(e?.message || "Failed to create clinic");
    } finally {
      setCreating(false);
    }
  };

  const inputCls = [
    "w-full",
    "border border-slate-200",
    "rounded-xl",
    "px-3 py-2",
    "font-body text-slate-800",
    "placeholder:text-slate-400",
    "focus:outline-none focus:ring-2 focus:ring-[#f3dfae]/60 focus:border-[#f3dfae]/80"
  ].join(" ");

  const labelCls = "font-ui text-xs uppercase tracking-[0.18em] text-slate-600";

  return (
    <section className="rounded-2xl border p-5 bg-white" style={{ borderColor: `${GOLD}B3` }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-ui font-bold uppercase tracking-[0.18em] text-slate-800">
            Create New Clinic
          </h2>
          <p className="mt-1 font-body text-sm text-slate-600">
            Add a single clinic. Use the bulk uploader for large imports.
          </p>
        </div>

        <button
          type="button"
          onClick={handleCreate}
          disabled={creating || !name.trim() || !slug.trim() || !shortDesc.trim()}
          className={[
            "inline-flex items-center justify-center rounded-full",
            "bg-[#f3dfae] px-6 py-3",
            "text-[#151A3A] font-ui font-bold uppercase tracking-[0.18em]",
            "hover:opacity-95 transition-opacity",
            "disabled:opacity-50"
          ].join(" ")}
        >
          {creating ? "Creating…" : "Create"}
        </button>
      </div>

      {err && (
        <div className="mt-4 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
          {err}
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <div className={labelCls}>Name</div>
          <input
            className={inputCls}
            placeholder="Clinic name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>Slug</div>
          <input
            className={inputCls}
            placeholder="slug-example"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>

        <div className="sm:col-span-2 space-y-1">
          <div className={labelCls}>Short description</div>
          <input
            className={inputCls}
            placeholder="Short description"
            value={shortDesc}
            onChange={(e) => setShortDesc(e.target.value)}
          />
        </div>

        <div className="sm:col-span-2 space-y-1">
          <div className={labelCls}>Long description</div>
          <textarea
            className={[inputCls, "min-h-[96px] resize-y"].join(" ")}
            placeholder="Long description (optional)"
            value={longDesc}
            onChange={(e) => setLongDesc(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>City</div>
          <input
            className={inputCls}
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>Country</div>
          <input
            className={inputCls}
            placeholder="Country (e.g., US, GB)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>Postcode</div>
          <input
            className={inputCls}
            placeholder="Postcode (for geocoding)"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
          />
        </div>

        <div className="sm:col-span-2 space-y-1">
          <div className={labelCls}>Website URL</div>
          <input
            className={inputCls}
            placeholder="Website URL"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>Phone</div>
          <input
            className={inputCls}
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <div className={labelCls}>Email</div>
          <input
            className={inputCls}
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-5 border-t border-slate-200 pt-4">
        <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
          Required: name, slug, short description
        </div>
      </div>
    </section>
  );
};

export default SingleClinicForm;
